import { Relations } from "@inrev/common";
import { type JSX, type ReactNode, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { cn } from "../lib/utils";
import { ResizeHandle } from "../patterns/resize-handle";
import { ClickableIcon, Text } from "../primitives";
import { ToolsPanelWidthContext } from "./ToolsPanelWidthContext";
import { useResizableToolsWidth } from "./useResizableToolsWidth";

export type ToolMapEntry = {
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	icon: (props: any) => JSX.Element;
	content: ReactNode;
	title?: string;
	headerActions?: ReactNode;
};

export type ToolMap = Record<string, ToolMapEntry>;

export const ToolsLayout = ({
	sidebar,
	expandedSidebar,
}: {
	sidebar: ReactNode;
	expandedSidebar: ReactNode;
}) => {
	return (
		<div className="w-fit h-full">
			{sidebar}
			{expandedSidebar}
		</div>
	);
};

export const ToolsSidebarLayout = <TToolMap extends ToolMap>({
	toolMap,
	setCurrentTool,
	currentTool,
	relation,
}: {
	toolMap: TToolMap;
	currentTool: keyof TToolMap | undefined;
	relation: Relations.ActionItems | Relations.Notes;
	setCurrentTool: (toolKey: keyof TToolMap) => void;
}) => {
	if (currentTool !== undefined) return <></>;
	return (
		<div className="flex flex-col h-full w-[48px] gap-2 bg-white items-center pt-5 border-l border-l-gray-100">
			{Object.keys(toolMap).map((toolKey, idx) => {
				const ToolIcon = toolMap[toolKey].icon;
				return (
					<ToolIcon
						key={idx}
						relation={relation}
						className={cn(
							"h-[19px] w-[19px] flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-800",
							currentTool && "text-gray-800 cursor-default",
						)}
						onClick={() => setCurrentTool(toolKey)}
					/>
				);
			})}
		</div>
	);
};

const useElementWidth = (fallback: number) => {
	const [element, setElement] = useState<HTMLDivElement | null>(null);
	const [measured, setMeasured] = useState<number | undefined>();

	useEffect(() => {
		if (!element) return;
		const observer = new ResizeObserver(([entry]) => {
			const width = entry?.borderBoxSize?.[0]?.inlineSize ?? entry?.contentRect.width ?? 0;
			if (width > 0) setMeasured(Math.round(width));
		});
		observer.observe(element);
		return () => {
			observer.disconnect();
			setMeasured(undefined);
		};
	}, [element]);

	return { element, ref: setElement, width: measured ?? fallback };
};

/** Main-column content can reflow a moment after a window resize, so the fit is measured again. */
const RESIZE_SETTLE_MS = 200;

/**
 * The widest the panel can be without running past the viewport's right edge. A main column
 * with a min width (and the app nav) can stop the row from shrinking, which pushes the panel
 * off-screen instead. On a narrow enough window this is below the panel's usual minimum. Measured before paint by briefly laying the panel out at `limit` and
 * seeing how far it overflows, so the result does not depend on the current width. Measured
 * again when the window resizes (at most once a frame, and once it settles).
 */
const useViewportFitWidth = (element: HTMLElement | null, limit: number) => {
	const [fit, setFit] = useState<number | undefined>();
	const [resizes, setResizes] = useState(0);

	useLayoutEffect(() => {
		if (!element) return;
		const previous = element.style.width;
		element.style.width = `${limit}px`;
		const rect = element.getBoundingClientRect();
		element.style.width = previous;
		const overflow = rect.right - document.documentElement.clientWidth;
		setFit(
			rect.width > 0 && overflow >= 1 ? Math.max(0, Math.floor(rect.width - overflow)) : undefined,
		);
	}, [element, limit, resizes]);

	useEffect(() => {
		let settle: ReturnType<typeof setTimeout> | undefined;
		let frame: number | undefined;
		const bump = () => setResizes((count) => count + 1);
		const onResize = () => {
			if (frame === undefined) {
				frame = requestAnimationFrame(() => {
					frame = undefined;
					bump();
				});
			}
			clearTimeout(settle);
			settle = setTimeout(bump, RESIZE_SETTLE_MS);
		};
		window.addEventListener("resize", onResize);
		return () => {
			window.removeEventListener("resize", onResize);
			clearTimeout(settle);
			if (frame !== undefined) cancelAnimationFrame(frame);
		};
	}, []);

	return fit;
};

type ToolsPanelProps<TToolMap extends ToolMap> = {
	toolMap: TToolMap;
	currentTool: keyof TToolMap;
	relation: Relations.ActionItems | Relations.Notes;
	close: () => void;
	setCurrentTool: (toolKey: keyof TToolMap) => void;
};

export const ToolsExpandedLayout = <TToolMap extends ToolMap>({
	currentTool,
	...props
}: Omit<ToolsPanelProps<TToolMap>, "currentTool"> & {
	currentTool: keyof TToolMap | undefined;
}) => {
	if (!currentTool) return <></>;
	return <ToolsPanel currentTool={currentTool} {...props} />;
};

const ToolsPanel = <TToolMap extends ToolMap>({
	toolMap,
	currentTool,
	relation,
	close,
	setCurrentTool,
}: ToolsPanelProps<TToolMap>) => {
	const {
		width: requestedWidth,
		min: preferredMin,
		max: viewportMax,
		defaultWidth,
		setWidth: setRequestedWidth,
		commitWidth,
	} = useResizableToolsWidth();
	const { element, ref, width: panelWidth } = useElementWidth(requestedWidth);
	const fit = useViewportFitWidth(element, viewportMax);
	const max = Math.min(viewportMax, fit ?? viewportMax);
	const min = Math.min(preferredMin, max);
	const width = Math.min(requestedWidth, max);

	const setWidth = useCallback(
		(next: number) => setRequestedWidth(Math.min(next, max)),
		[max, setRequestedWidth],
	);
	const commitFitWidth = useCallback(
		(next: number) => commitWidth(Math.min(next, max)),
		[max, commitWidth],
	);

	return (
		<div
			ref={ref}
			style={{ width }}
			className="relative z-30 flex h-full shrink-0 flex-col justify-start border-l border-l-bc-default bg-surface-base shadow-panel-edge"
			data-testid="tools-panel"
		>
			<ResizeHandle
				width={width}
				min={min}
				max={max}
				defaultWidth={defaultWidth}
				onWidthChange={setWidth}
				onWidthCommit={commitFitWidth}
				label="Resize tools panel"
			/>
			<ToolsPanelWidthContext.Provider value={panelWidth}>
				<ToolsTopBar
					toolMap={toolMap}
					currentTool={currentTool}
					relation={relation}
					close={close}
					setCurrentTool={setCurrentTool}
				/>
				<div className="relative block min-h-0 flex-1">{toolMap[currentTool].content}</div>
			</ToolsPanelWidthContext.Provider>
		</div>
	);
};

const ToolsTopBar = <TToolMap extends ToolMap>({
	toolMap,
	currentTool,
	relation,
	setCurrentTool,
	close,
}: {
	toolMap: TToolMap;
	currentTool: keyof TToolMap;
	relation: Relations.ActionItems | Relations.Notes;
	setCurrentTool: (toolKey: keyof TToolMap) => void;
	close: () => void;
}) => {
	const { headerActions } = toolMap[currentTool];
	return (
		<div className="z-30 flex h-10 shrink-0 items-center justify-between gap-1.5 border-b border-bc-default bg-surface-base pl-1 pr-2">
			<div className="flex h-full min-w-0 items-center">
				{Object.keys(toolMap).map((toolKey) => {
					const { icon: ToolIcon, title } = toolMap[toolKey];
					const active = toolKey === currentTool;
					return (
						<div
							key={toolKey}
							data-testid={`tools-tab-${toolKey}`}
							aria-current={active || undefined}
							className={cn(
								"relative flex h-full items-center gap-1",
								active ? "min-w-0 px-2" : "shrink-0 px-1",
							)}
						>
							<ToolIcon
								relation={relation}
								className={
									active
										? "cursor-default text-primary hover:text-primary"
										: "cursor-pointer text-tertiary hover:text-primary"
								}
								onClick={() => {
									if (!active) setCurrentTool(toolKey);
								}}
							/>
							{active && title && (
								<Text type="body-strong" color="primary" truncate className="text-[13px]">
									{title}
								</Text>
							)}
							{active && (
								<span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-accent" />
							)}
						</div>
					);
				})}
			</div>
			<div className="flex shrink-0 items-center gap-1">
				{headerActions}
				<ClickableIcon
					type="chevron-double-right"
					size="mini"
					color="tertiary"
					aria-label="Close tools"
					tooltip="Close"
					onClick={close}
				/>
			</div>
		</div>
	);
};
